import type Util from "./util";

/**
 * Contains several utility functions related with HTML manipulation.
 */
export default class HTMLUtil {
    private util: Util;

    constructor(util: Util) {
        this.util = util;
    }

    /**
     * Inserts <a href...> into strings if it finds links (http/https/ftp/email) on it
     * 
     * @param str 
     * 
     * @returns 
     */
    linkify(str: string): string {
        // URLs starting with http://, https://, or ftp://
        str = str.replace(/(\b(https?|ftp):\/\/[-A-Z0-9+&@#$\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim, '<a href="$1" target="_blank">$1</a>');
    
        // URLs starting with "www." (without // before it, or it'd re-link the ones done above).
        str = str.replace(/(^|[^\/])(www\.[\S]+(\b|$))/gim, '$1<a href="http://$2" target="_blank">$2</a>');
    
        // Change email addresses to mailto:: links.
        // RegExp taken from http://www.regular-expressions.info/email.html a-z0-9_ replaced with \w
        str = str.replace(/[\w.%+-]+@(?:[\w-]+\.)+[a-z]{2,}/gim, '<a href="mailto:$&">$&</a>'); // TOR9-5632 $1 was not being replaced, $& works...

        return str;
    }
    
    /**
     * Replace string html entities with encoded text
     * 
     * @param str The original string (ex: ">10")
     * @param isNewLineBR should replace \n chars in string with <br />
     * 
     * @returns encoded text (ex: "&gt;10")
     */
    encode(str: string|null, isNewLineBR?: boolean) {
        if (!str)
            return "";
        
        str = $('<div/>').text(str).html();
        if (isNewLineBR) {
            str = str.replaceAll('\n', '<br />');
        }
        
        return str;
    }

    /**
     * Replace string encoded html entities with html
     * 
     * @param str The original string (ex: "&gt;10")
     * @return decoded text (ex: ">10")
     */
    decode(str: string) {
        if (!str)
            return "";
        
        str = $('<div/>').html(str).text();
        
        return str;
    }
    
    /**
     * Gets the next highest z-index, i.e. the z-index that can be used by a element to ensure is on top of all elements displayed in the page.
     * 
     * @returns
     */
    getNextHighestZindex(elements?: HTMLCollectionOf<Element>) {
        let highestIndex = 0;
        const elArray = elements || document.getElementsByTagName('*');
        for (let i=0; i < elArray.length; i++) {
            let currentIndex = 0;
            
            if (elArray[i].style && elArray[i].style['zIndex'] != '') {
                currentIndex = parseInt(elArray[i].style['zIndex']);
            } else if (window.getComputedStyle) {
                currentIndex = parseInt(document.defaultView.getComputedStyle(elArray[i], null).getPropertyValue('z-index'));
            }

            if (!isNaN(currentIndex) && currentIndex > highestIndex) {
                highestIndex = currentIndex;
            }
        }
        
        return highestIndex + 1;
    }
    
    /**
     * Returns the caret (cursor) position of the specified text field.
     * 
     * @param inputEl 
     * @returns 
     */
    getCaretPosition(inputEl: HTMLInputElement) {
        let pos = 0;
        
        if (document.selection) { // IE Support
            inputEl.focus();
            var range = document.selection.createRange();
            range.moveStart ('character', -inputEl.value.length);
            pos = range.text.length;
        } else if (inputEl.selectionStart || inputEl.selectionStart == '0') { // Firefox support
            pos = inputEl.selectionStart;
        }
        
        return pos;
    };
    
    /**
     * Returns the caret (cursor) position in the specified text field.
     * 
     * @param inputEl 
     * @param pos 
     */
    setCaretPosition(inputEl: HTMLInputElement, pos: number) {
        if (inputEl.setSelectionRange) {
            inputEl.focus();
            inputEl.setSelectionRange(pos, pos);
        } else if (inputEl.createTextRange) {
            const range = inputEl.createTextRange();
            
            range.collapse(true);
            range.moveEnd('character', pos);
            range.moveStart('character', pos);
            range.select();
        }
    }
    
    /**
     * 
     * @param inputEl 
     * @param getValidFn 
     */
    private forceValidInput(inputEl: HTMLInputElement, getValidFn: Function) {
        const jEl = $(inputEl);
        
        jEl.on('input', () => {
            const val = jEl.val();
            const newVal = getValidFn(val);
            
            if (newVal != val) {
                const pos = this.getCaretPosition(jEl[0]);
                const isEnd = (pos == val.length);
                let newPos;
                if (isEnd || pos > newVal.length) {
                    newPos = newVal.length;
                } else {
                    newPos = pos;
                    const i = newVal.indexOf('.');
                    if (i == -1 || i + 1 != pos) {
                        newPos -= val.length - newVal.length;
                        
                        if (newPos < 0) {
                            newPos = 0;
                        }
                    }
                }
                
                jEl.val(newVal);
                this.setCaretPosition(jEl[0], newPos);
            }
        });
        
        jEl.on('change', function() {
            if (jEl.val() == '-') {
                jEl.val('');
            }
        });
    }
    
    /**
     * Forces an input to accept only integer numbers
     * 
     * @param inputEl 
     */
    forceIntegerInput(inputEl: HTMLInputElement) {
        this.forceValidInput(inputEl, this.util.getValidIntegerString);
    }
    
    /**
     * Forces an input to accept only decimal numbers
     * 
     * @param inputEl 
     */
    forceDecimalInput(inputEl: HTMLInputElement) {
        this.forceValidInput(inputEl, this.util.getValidDecimalString);
    }
}
